import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  EyeIcon,
  CursorArrowRaysIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';

interface TopPerformingCampaign {
  id: string;
  name: string;
  status: string;
  totalImpressions: number;
  totalClicks: number;
  ctr: number;
  totalSpend: number;
  averageCPC: number;
  trend: 'up' | 'down' | 'stable';
  trendPercentage: number;
  lastActivity: string;
  // Yeni alanlar
  currentImpressions?: number;
  targetImpressions?: number;
  impressionRatio?: number;
}

interface TopPerformingCampaignsProps {
  campaigns: TopPerformingCampaign[];
  loading?: boolean;
  error?: string;
}

const TopPerformingCampaigns: React.FC<TopPerformingCampaignsProps> = ({
  campaigns,
  loading = false,
  error
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatSpendRatio = (ratio: number): string => {
    return `${ratio.toFixed(1)}%`;
  };

  const formatImpressionRatio = (current: number, target: number): string => {
    if (target === 0) return '0/0';
    return `${formatNumber(current)}/${formatNumber(target)}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      active: 'Aktif',
      paused: 'Duraklatıldı',
      draft: 'Taslak',
      completed: 'Tamamlandı'
    };
    return labels[status] || status;
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />;
      case 'down':
        return <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />;
      case 'stable':
        return <MinusIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTrendColor = (trend: 'up' | 'down' | 'stable'): string => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      case 'stable':
        return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">En İyi Performans Gösteren Kampanyalar</h3>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-gray-200 rounded"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </div>
                  <div className="w-20 h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">En İyi Performans Gösteren Kampanyalar</h3>
          <div className="text-center py-4">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">En İyi Performans Gösteren Kampanyalar</h3>
          <Link
            to="/campaigns"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            Tümünü Gör
          </Link>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-8">
            <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">Henüz kampanya verisi bulunmuyor</p>
          </div>
        ) : (
          <div className="space-y-4">
            {campaigns.map((campaign, index) => (
              <div
                key={campaign.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-semibold text-blue-600">#{index + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/campaigns/${campaign.id}`}
                          className="text-sm font-medium text-gray-900 hover:text-blue-600 truncate block"
                        >
                          {campaign.name}
                        </Link>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(campaign.status)}`}
                        >
                          {getStatusLabel(campaign.status)}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-3">
                      <div className="flex items-center space-x-2">
                        <EyeIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Görüntülenme</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {formatNumber(campaign.totalImpressions)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <CursorArrowRaysIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">CTR</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaign.ctr}%
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <BanknotesIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Harcama</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaign.currentImpressions !== undefined && campaign.targetImpressions !== undefined
                              ? formatImpressionRatio(campaign.currentImpressions, campaign.targetImpressions)
                              : formatSpendRatio(campaign.totalSpend)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        {getTrendIcon(campaign.trend)}
                        <div>
                          <p className="text-xs text-gray-500">Trend</p>
                          <p className={`text-sm font-semibold ${getTrendColor(campaign.trend)}`}>
                            {campaign.trend === 'stable' ? 'Stabil' : `${campaign.trendPercentage}%`}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopPerformingCampaigns; 